/**
 * Lettre motion layer: scroll reveals, scroll-linked progress, parallax, magnetic buttons,
 * and the hero, marquee, alphabet, and set-card components. Vanilla, no dependencies.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Horizon scrolls .page-wrapper instead of the window on desktop, so scroll position comes from whichever element scrolls.
function scrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight) return wrapper.scrollTop;
  return window.scrollY;
}
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

// A fully clipped element never reports as intersecting, so clip reveals are observed through their parent.
const revealProxies = new WeakMap();

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const targets = revealProxies.get(entry.target) || [entry.target];
      for (const target of targets) target.classList.add('is-in');
      revealProxies.delete(entry.target);
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
);

function observeReveals(root = document) {
  const viewportBottom = window.innerHeight;
  for (const el of root.querySelectorAll('[data-l-reveal]:not(.is-in), .l-line:not(.is-in)')) {
    const rect = el.getBoundingClientRect();
    if (reducedMotion.matches || (rect.top < viewportBottom && rect.bottom > 0)) {
      el.classList.add('is-in');
      continue;
    }
    const proxy = el.dataset.lReveal === 'clip' && el.parentElement ? el.parentElement : el;
    if (proxy !== el) {
      const proxyReveals = proxy.matches('[data-l-reveal], .l-line') ? [proxy] : [];
      const existing = revealProxies.get(proxy) || proxyReveals;
      revealProxies.set(proxy, [...existing, el]);
    }
    revealObserver.observe(proxy);
  }
}

// Per-frame scroll work runs only for elements near the viewport.
const active = new Set();
const proximityObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) active.add(entry.target);
      else active.delete(entry.target);
    }
    requestTick();
  },
  { rootMargin: '25% 0px 25% 0px' }
);

let ticking = false;
function requestTick() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(updateScrollEffects);
}

function updateScrollEffects() {
  ticking = false;
  const vh = window.innerHeight;
  for (const el of active) {
    const rect = el.getBoundingClientRect();
    if (el.hasAttribute('data-l-progress')) {
      const start = parseFloat(el.dataset.lProgressStart || '0.85');
      const end = parseFloat(el.dataset.lProgressEnd || '0.25');
      const raw = (vh * start - rect.top) / (rect.height + vh * (start - end));
      el.style.setProperty('--l-progress', Math.min(1, Math.max(0, raw)).toFixed(4));
    }
    if (el.hasAttribute('data-l-parallax') && !reducedMotion.matches) {
      const speed = parseFloat(el.dataset.lParallax || '0.1');
      const offset = (rect.top + rect.height / 2 - vh / 2) * speed;
      el.style.setProperty('--l-parallax', `${(-offset).toFixed(1)}px`);
    }
  }
}

function observeScrollEffects(root = document) {
  for (const el of root.querySelectorAll('[data-l-progress], [data-l-parallax]')) {
    proximityObserver.observe(el);
  }
}

function bindMagnetic(root = document) {
  if (!finePointer.matches || reducedMotion.matches) return;
  for (const el of root.querySelectorAll('[data-l-magnetic]:not([data-l-bound])')) {
    el.setAttribute('data-l-bound', '');
    const strength = parseFloat(el.dataset.lMagnetic || '0.3');
    el.addEventListener('pointermove', (event) => {
      const rect = el.getBoundingClientRect();
      const x = (event.clientX - rect.left - rect.width / 2) * strength;
      const y = (event.clientY - rect.top - rect.height / 2) * strength;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
    el.addEventListener('pointerleave', () => {
      el.style.transform = '';
    });
  }
}

class LettreHero extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.layers = [...this.querySelectorAll('[data-l-glyph]')];
    this.counter = this.querySelector('[data-l-counter]');
    this.letters = (this.dataset.letters || '').replace(/\s/g, '').split('');
    this.index = 0;
    this.front = 0;

    if (finePointer.matches && !reducedMotion.matches) {
      let frame = 0;
      this.addEventListener(
        'pointermove',
        (event) => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const rect = this.getBoundingClientRect();
            this.style.setProperty('--l-px', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3));
            this.style.setProperty('--l-py', ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3));
          });
        },
        { signal }
      );
    }

    if (reducedMotion.matches || this.letters.length < 2 || this.layers.length < 2) return;

    this.visible = true;
    this.visibility = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
    });
    this.visibility.observe(this);
    this.timer = window.setInterval(() => {
      if (this.visible && !document.hidden) this.advance();
    }, parseInt(this.dataset.interval || '2600', 10));
  }

  advance() {
    this.index = (this.index + 1) % this.letters.length;
    const outgoing = this.layers[this.front];
    this.front = 1 - this.front;
    const incoming = this.layers[this.front];
    incoming.textContent = this.letters[this.index];

    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';
    outgoing.animate(
      [
        { opacity: 1, filter: 'blur(0px)', transform: 'translate3d(0,0,0) scale(1) rotate(0deg)' },
        { opacity: 0, filter: 'blur(18px)', transform: 'translate3d(-4%, -3%, 0) scale(1.08) rotate(-3deg)' },
      ],
      { duration: 900, easing, fill: 'forwards' }
    );
    incoming.animate(
      [
        { opacity: 0, filter: 'blur(18px)', transform: 'translate3d(5%, 4%, 0) scale(0.9) rotate(4deg)' },
        { opacity: 1, filter: 'blur(0px)', transform: 'translate3d(0,0,0) scale(1) rotate(0deg)' },
      ],
      { duration: 1300, easing, delay: 120, fill: 'forwards' }
    );

    if (this.counter) {
      const letter = this.letters[this.index];
      const position = String(letter.charCodeAt(0) - 64).padStart(2, '0');
      this.counter.textContent = `${letter} — ${position} / 26`;
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.visibility?.disconnect();
    window.clearInterval(this.timer);
  }
}

class LettreMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-l-track]');
    if (!this.track || reducedMotion.matches) return;
    this.animation = this.track.animate([{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(-50%,0,0)' }], {
      duration: parseInt(this.dataset.duration || '38000', 10),
      iterations: Infinity,
    });
    this.lastY = scrollTop();
    this.boost = 0;
    this.direction = 1;
    this.onScroll = () => {
      const y = scrollTop();
      const delta = y - this.lastY;
      this.lastY = y;
      this.boost = Math.min(5, Math.abs(delta) / 12);
      this.direction = delta >= 0 ? 1 : -1;
    };
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    const loop = () => {
      this.boost *= 0.92;
      this.animation.playbackRate = (1 + this.boost) * this.direction;
      this.style.setProperty('--l-skew', `${(this.boost * 1.2 * this.direction).toFixed(2)}deg`);
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    cancelAnimationFrame(this.frame);
    this.animation?.cancel();
  }
}

class LettreAlphabet extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.preview = this.querySelector('[data-l-preview]');
    this.names = this.querySelector('[data-l-names]');
    this.cta = this.querySelector('[data-l-cta]');
    for (const letter of this.querySelectorAll('[data-l-letter]')) {
      const select = () => this.select(letter);
      letter.addEventListener('pointerenter', select, { signal });
      letter.addEventListener('focus', select, { signal });
    }
  }

  select(letter) {
    if (this.current === letter) return;
    this.current?.classList.remove('is-active');
    this.current = letter;
    letter.classList.add('is-active');
    const value = letter.dataset.lLetter || '';
    if (this.preview) {
      this.preview.textContent = value;
      if (!reducedMotion.matches) {
        this.preview.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 8%, 0) rotate(-4deg)', filter: 'blur(10px)' },
            { opacity: 1, transform: 'none', filter: 'blur(0px)' },
          ],
          { duration: 800, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
        );
      }
    }
    if (this.names) this.names.textContent = letter.dataset.lNames || '';
    if (this.cta && letter instanceof HTMLAnchorElement) {
      this.cta.href = letter.href;
      const label = this.cta.querySelector('[data-l-cta-letter]');
      if (label) label.textContent = value;
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

class LettreCard extends HTMLElement {
  connectedCallback() {
    if (!finePointer.matches) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener(
      'pointermove',
      (event) => {
        const rect = this.getBoundingClientRect();
        this.style.setProperty('--l-cx', `${event.clientX - rect.left}px`);
        this.style.setProperty('--l-cy', `${event.clientY - rect.top}px`);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

for (const [name, ctor] of [
  ['lettre-hero', LettreHero],
  ['lettre-marquee', LettreMarquee],
  ['lettre-alphabet', LettreAlphabet],
  ['lettre-card', LettreCard],
]) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

function init(root = document) {
  observeReveals(root);
  observeScrollEffects(root);
  bindMagnetic(root);
  requestTick();
}

document.addEventListener('scroll', requestTick, { passive: true, capture: true });
window.addEventListener('resize', requestTick, { passive: true });
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});

init();
